import { useToastStore } from "@/stores/toast.store";
import { useShare } from "@vueuse/core";
import { toValue, type MaybeRef } from "vue";
import isMobile from "is-mobile";
import { useI18n } from "vue-i18n";
import {
  writeToClipboard,
  share as navigatorShare,
} from "@/utils/app-navigator";
import { useAppStore } from "@/stores/app.store";

interface SharePayload {
  title: string | undefined;
  text: string | undefined;
  url: string | undefined;
}

export function useCopyToClipboard() {
  const toastStore = useToastStore();
  const { t } = useI18n();
  async function copyToClipboard(value: MaybeRef<string | ClipboardItem>) {
    const body = toValue(value);
    try {
      // app-navigator routes the call through the host SDK when embedded
      // (so the clipboard write happens on the host page, where permission
      // is granted). Standalone, falls back to navigator.clipboard.
      await writeToClipboard(body);

      toastStore.addToast({
        message: t("ui.alerts.linkCopied"),
        type: "success",
      });
    } catch (error) {
      console.error(
        "[ShareOrCopy] Error received while attempting to copy text to clipboard",
        body,
        error,
      );

      toastStore.addToast({
        message: t("ui.alerts.unableToShareViaClipboard"),
        type: "success",
      });
    }
  }
  return copyToClipboard;
}

/**
 * Shows the browser native share dialog if available, otherwise copies the text to the clipboard.
 *
 * In embed context the share/copy is relayed to the host page so the
 * native UI appears in the host's origin and the link points at the host.
 */
export function useShareOrCopyHandler() {
  const copyToClipboard = useCopyToClipboard();
  const { isSupported: isShareSupported } = useShare();
  const appStore = useAppStore();

  async function safeShare(
    shareObject: MaybeRef<SharePayload>,
    fallbackClipboardContent: MaybeRef<string | ClipboardItem>,
  ) {
    // Standalone heuristic preserved: only use the native share dialog on
    // mobile when supported. In embed context we always try the relay so
    // the host's mobile share sheet opens — the host runs on the user's
    // primary domain and is the right place for a system-level share.
    const inEmbed = appStore.isEmbed;
    if (!inEmbed && (!isShareSupported.value || !isMobile())) {
      copyToClipboard(fallbackClipboardContent);
      return;
    }

    const object = toValue(shareObject);
    try {
      await navigatorShare({
        title: object.title,
        text: object.text,
        url: object.url ?? "",
      });
    } catch (error) {
      // Either the native share aborted (user cancel — common) or the
      // host SDK isn't available; fall back to copying so the link is
      // still in the clipboard for the user.
      console.error(
        "[ShareOrCopy] Share relay failed, falling back to clipboard",
        error,
      );
      copyToClipboard(fallbackClipboardContent);
    }
  }

  return safeShare;
}

export function useShareOrCopy(
  shareObject: MaybeRef<SharePayload>,
  fallbackClipboardText: MaybeRef<string>,
) {
  const safeShare = useShareOrCopyHandler();

  return () => safeShare(shareObject, fallbackClipboardText);
}
